import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import ExpandMore from 'src/icons/ExpandMore';

import type { MutableRefObject, ReactNode } from 'react';

export type SectionId = 'rules' | 'training' | 'performance' | 'funnel' | 'business' | 'knowledge' | 'checkout' | 'purchases' | 'economics';
export type PageSection = { id: SectionId; title: string };
export type StreamNavigationHandle = { openSection: (id: SectionId) => void };
const Context = createContext<{
    sections: PageSection[];
    collapsed: Partial<Record<SectionId, boolean>>;
    active: SectionId;
    openSection: (id: SectionId) => void;
    toggle: (id: SectionId) => void;
    register: (id: SectionId, node: HTMLElement | null) => void;
    setHeader: (node: HTMLDivElement | null) => void;
} | null>(null);

export function StreamPageSections({
    children,
    sections,
    initialSection,
    navigationRef,
    defaultCollapsed = {},
}: {
    children: ReactNode;
    sections: PageSection[];
    initialSection?: string;
    navigationRef: MutableRefObject<StreamNavigationHandle | null>;
    defaultCollapsed?: Partial<Record<SectionId, boolean>>;
}) {
    const [collapsed, setCollapsed] = useState<Partial<Record<SectionId, boolean>>>(defaultCollapsed);
    const [active, setActive] = useState<SectionId>(sections[0]?.id || 'rules');
    const [pending, setPending] = useState<SectionId | null>(null);
    const [version, setVersion] = useState(0);
    const [header, setHeader] = useState<HTMLDivElement | null>(null);
    const root = useRef<HTMLDivElement>(null);
    const nodes = useRef(new Map<SectionId, HTMLElement>());
    const register = useCallback((id: SectionId, node: HTMLElement | null) => {
        if (node) nodes.current.set(id, node);
        else nodes.current.delete(id);
        setVersion((v) => v + 1);
    }, []);
    const openSection = useCallback((id: SectionId) => {
        setCollapsed((value) => ({ ...value, [id]: false }));
        setActive(id);
        setPending(id);
    }, []);
    const toggle = useCallback((id: SectionId) => {
        setCollapsed((value) => ({ ...value, [id]: !value[id] }));
    }, []);
    useLayoutEffect(() => {
        navigationRef.current = { openSection };
        return () => {
            navigationRef.current = null;
        };
    }, [navigationRef, openSection]);
    const sectionIds = sections.map((section) => section.id).join(',');
    useEffect(() => {
        if (initialSection && sectionIds.split(',').includes(initialSection)) openSection(initialSection as SectionId);
    }, [initialSection, sectionIds, openSection]);
    useLayoutEffect(() => {
        if (!header) return;
        const measure = () => {
            const top = parseFloat(getComputedStyle(header).top) || 0;
            root.current?.style.setProperty(
                '--stream-header-offset',
                `${header.getBoundingClientRect().height + top + 16}px`
            );
        };
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(header);
        window.addEventListener('resize', measure);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [header]);
    useEffect(() => {
        const node = pending ? nodes.current.get(pending) : null;
        if (!node) return;
        let frame = 0;
        let previousPosition = '';
        const align = () => {
            const position = `${node.getBoundingClientRect().top + window.scrollY}:${
                header?.getBoundingClientRect().height
            }`;
            if (position === previousPosition) return;
            previousPosition = position;
            node.scrollIntoView({ block: 'start', behavior: 'auto' });
            node.querySelector<HTMLButtonElement>('[aria-expanded]')?.focus({ preventScroll: true });
        };
        const schedule = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(align);
        };
        // Keep a deep link aligned while async sections above it finish loading.
        // Any user interaction ends this, so normal scrolling is never pulled back.
        const release = () => setPending(null);
        const events = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
        events.forEach((name) => window.addEventListener(name, release, { passive: true }));
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
        if (root.current) observer?.observe(root.current);
        if (header) observer?.observe(header);
        schedule();
        return () => {
            cancelAnimationFrame(frame);
            observer?.disconnect();
            events.forEach((name) => window.removeEventListener(name, release));
        };
    }, [pending, collapsed, version, header]);
    useEffect(() => {
        let frame = 0;
        const update = () => {
            if (pending) return;
            const offset = (header?.getBoundingClientRect().bottom || 0) + 28;
            let current = sectionIds.split(',')[0] as SectionId;
            for (const id of sectionIds.split(',') as SectionId[]) {
                if ((nodes.current.get(id)?.getBoundingClientRect().top ?? Infinity) <= offset) current = id;
            }
            setActive(current);
        };
        const onScroll = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(update);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        update();
        return () => {
            window.removeEventListener('scroll', onScroll);
            cancelAnimationFrame(frame);
        };
    }, [header, sectionIds, pending]);
    return (
        <Context.Provider value={{ sections, collapsed, active, openSection, toggle, register, setHeader }}>
            <Box
                ref={root}
                data-stream-page
                sx={{ '--stream-header-offset': '220px', '--stream-sticky-top': { xs: '48px', md: '0px' } }}
            >
                {children}
            </Box>
        </Context.Provider>
    );
}

export function StickyStreamHeader({ children }: { children: ReactNode }) {
    const context = useContext(Context);
    return (
        <Box
            ref={context?.setHeader}
            sx={{
                position: 'sticky',
                top: 'var(--stream-sticky-top, 0px)',
                zIndex: 20,
                bgcolor: 'var(--lm-canvas)',
                py: 1,
            }}
        >
            {children}
        </Box>
    );
}

export function StreamSection({
    id,
    title,
    children,
    summary,
    plain = false,
}: {
    id: SectionId;
    title: string;
    children: ReactNode;
    summary?: ReactNode;
    plain?: boolean;
}) {
    const context = useContext(Context);
    const [localCollapsed, setLocalCollapsed] = useState(false);
    const register = context?.register;
    const sectionRef = useCallback((node: HTMLElement | null) => register?.(id, node), [register, id]);
    const closed = context ? !!context.collapsed[id] : localCollapsed;
    const openPlain = plain && !closed;
    return (
        <Box
            ref={sectionRef}
            component="section"
            id={id}
            aria-labelledby={`${id}-heading`}
            sx={{
                scrollMarginTop: 'var(--stream-header-offset, 220px)',
                border: openPlain ? 'none' : '1px solid var(--lm-border)',
                borderRadius: '8px',
                bgcolor: openPlain ? 'transparent' : 'var(--lm-surface)',
                minWidth: 0,
            }}
        >
            <Typography
                component="h2"
                id={`${id}-heading`}
                sx={{ m: 0 }}
            >
                <Button
                    fullWidth
                    disableRipple
                    type="button"
                    aria-expanded={!closed}
                    aria-controls={`${id}-body`}
                    onClick={() => (context ? context.toggle(id) : setLocalCollapsed(!closed))}
                    sx={{
                        px: openPlain ? 0 : { xs: 2, sm: 2.5 },
                        py: openPlain ? 1 : 2,
                        height: 'auto',
                        minHeight: openPlain ? 48 : 60,
                        lineHeight: 1.35,
                        borderRadius: '8px',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        color: 'text.primary',
                        fontFamily: openPlain ? '"Inter", system-ui' : undefined,
                        fontSize: openPlain ? { xs: 26, sm: 28 } : 20,
                        fontWeight: openPlain ? 900 : 700,
                        letterSpacing: openPlain ? '-0.03em' : undefined,
                        textAlign: 'left',
                        textTransform: 'none',
                        '&:hover': { bgcolor: openPlain ? 'transparent' : 'var(--lm-hover)' },
                        '&.Mui-focusVisible': { boxShadow: 'inset 0 0 0 2px var(--lm-accent-ink)' },
                    }}
                >
                    <Box
                        component="span"
                        sx={{ minWidth: 0 }}
                    >
                        {title}
                    </Box>
                    <Box
                        component="span"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, maxWidth: '48%' }}
                    >
                        {closed && summary && (
                            <Box
                                component="span"
                                sx={{
                                    fontSize: 13,
                                    color: 'var(--lm-muted)',
                                    fontWeight: 400,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {summary}
                            </Box>
                        )}
                        <Box
                            component="span"
                            sx={{
                                display: 'flex',
                                flexShrink: 0,
                                color: 'var(--lm-muted)',
                                transform: closed ? 'none' : 'rotate(180deg)',
                            }}
                        >
                            <ExpandMore />
                        </Box>
                    </Box>
                </Button>
            </Typography>
            <Box
                id={`${id}-body`}
                hidden={closed}
                sx={{ px: plain ? 0 : { xs: 2, sm: 2.5 }, pb: plain ? 0 : { xs: 2, sm: 2.5 } }}
            >
                {children}
            </Box>
        </Box>
    );
}

export function StreamSectionNavigation({ mobile = false }: { mobile?: boolean }) {
    const context = useContext(Context);
    if (!context) return null;
    if (mobile)
        return (
            <Box sx={{ pt: 1 }}>
                <Box
                    component="select"
                    aria-label="Разделы потока"
                    value={context.active}
                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                        context.openSection(event.target.value as SectionId)
                    }
                    sx={{
                        width: '100%',
                        height: 38,
                        px: 1.5,
                        border: '1px solid var(--lm-border)',
                        borderRadius: '8px',
                        bgcolor: 'var(--lm-surface)',
                        color: 'var(--lm-text)',
                        font: 'inherit',
                        fontSize: 14,
                    }}
                >
                    {context.sections.map(({ id, title }) => (
                        <option
                            value={id}
                            key={id}
                        >
                            {title}
                        </option>
                    ))}
                </Box>
            </Box>
        );
    return (
        <Box
            component="nav"
            aria-label="Разделы потока"
            sx={{ px: 2, pb: 2 }}
        >
            <Typography sx={{ fontSize: 13, color: 'var(--lm-muted)', mb: 1, px: 1.5 }}>В этом потоке</Typography>
            <Stack spacing={0.25}>
                {context.sections.map(({ id, title }) => (
                    <Button
                        key={id}
                        component="a"
                        href={`#${id}`}
                        aria-current={context.active === id ? 'location' : undefined}
                        onClick={(event) => {
                            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                            event.preventDefault();
                            context.openSection(id);
                        }}
                        sx={{
                            justifyContent: 'flex-start',
                            px: 1.5,
                            py: 1,
                            minHeight: 42,
                            fontSize: 14,
                            fontWeight: context.active === id ? 600 : 400,
                            textTransform: 'none',
                            color: context.active === id ? 'var(--lm-accent-ink)' : 'text.primary',
                            bgcolor: context.active === id ? 'var(--lm-hover)' : 'transparent',
                            borderRadius: '8px',
                        }}
                    >
                        {title}
                    </Button>
                ))}
            </Stack>
        </Box>
    );
}
